import React from 'react'
import { SketchHomeButton, routes } from '@sketch/modules-common'
import {
  matchPath,
  useLocation,
  useParams,
  useRouteMatch,
} from 'react-router-dom'
import {
  Flex,
  useResponsiveDropdown,
  Dropdown,
  ButtonUnstyled,
  DocumentHead,
  useBreakpoint,
} from '@sketch/components'
import { GetPreviewDetailsQuery } from '@sketch/gql-types'
import {
  usePRFileArtboards,
  usePRFileSections,
} from '@sketch-hq/sketch-web-renderer'
import { PreviewTitle } from './PageCanvasViewHeader.styles'
import { usePreview } from '../../../views/PreviewView/PreviewContext'
import {
  PAGES_TEXT,
  PROTOTYPES_TEXT,
} from '../../../components/HeaderNavigation/PagesNavigationItem/constants'
import {
  ViewWrapper,
  StyledSeparator,
  DropdownItem,
  PrototypeIcon,
  PageIcon,
  ChevronsIcon,
} from '../../../components/HeaderNavigation/NavigationItem.styles'
import NavigationItem from '../../../components/HeaderNavigation/NavigationItem'
import { BreadcrumbDivider } from '../../../components/HeaderNavigation/Breadcrumbs.styles'

interface ViewsSelectorProps {
  preview: NonNullable<GetPreviewDetailsQuery['preview']> | undefined
  isMobile: boolean
}

const PreviewPagesDropdown: React.FCC<ViewsSelectorProps> = ({
  preview,
  isMobile,
}) => {
  const location = useLocation()

  const copyPerItem = {
    pages: PAGES_TEXT,
    prototypes: PROTOTYPES_TEXT,
  }

  if (!preview) {
    return null
  }

  const previewRoute = routes.PREVIEW_VIEW.create({
    previewID: preview.identifier,
  })
  const prototypeRoute = routes.PREVIEW_PROTOTYPES.create({
    previewID: preview.identifier,
  })

  const hasPrototypes = preview.prototypes.meta.totalCount > 0

  return (
    <ViewWrapper data-testid="views-dropdown">
      {isMobile && <StyledSeparator />}
      {!isMobile && <Dropdown.Header>Views</Dropdown.Header>}
      {!isMobile && (
        <DropdownItem
          key={PAGES_TEXT}
          icon={<PageIcon />}
          to={previewRoute}
          isActive={() =>
            !!matchPath(location.pathname, {
              path: previewRoute,
              exact: true,
            })
          }
        >
          {copyPerItem['pages']}
        </DropdownItem>
      )}

      {hasPrototypes && (
        <DropdownItem icon={<PrototypeIcon />} to={prototypeRoute}>
          {copyPerItem['prototypes']}
        </DropdownItem>
      )}
    </ViewWrapper>
  )
}

export const PreviewHeader = () => {
  const previewContext = usePreview()
  const isMobile = !useBreakpoint('xs')
  const location = useLocation()

  const isPrototypeRoute = !!useRouteMatch({
    path: routes.PREVIEW_PROTOTYPES.template(),
    exact: true,
  })
  const isFrameRoute = !!useRouteMatch({
    path: routes.PREVIEW_FRAME_VIEW.template(),
    exact: true,
  })
  const isSectionRoute = !!useRouteMatch({
    path: routes.PREVIEW_PAGE_SECTION.template(),
    exact: true,
  })

  const { pageUUID, frameUUID, sectionUUID } = useParams<{
    pageUUID?: string
    frameUUID?: string
    sectionUUID?: string
  }>()

  // Entity names sourced from the already-loaded PR file — no extra GQL query
  const prFileFrames = usePRFileArtboards()
  const currentFrameName = prFileFrames?.find(
    f => f.objectId === frameUUID
  )?.name
  const prFileSections = usePRFileSections()
  const currentSectionName = prFileSections?.find(
    section => section.objectId === sectionUUID
  )?.name

  const [content, buttonProps] = useResponsiveDropdown({
    dropdown: PreviewPagesDropdown,
    dropdownProps: {
      preview: previewContext?.preview,
      isMobile,
    },
    dropdownStyle: {
      maxHeight: '80vh',
      overflowY: 'auto',
    },
    usePortal: true,
  })

  if (!previewContext) {
    return null
  }

  const preview = previewContext.preview

  // Both focus views share the same breadcrumb: preview › page › entity.
  if ((isFrameRoute || isSectionRoute) && pageUUID) {
    const entityIcon = isSectionRoute ? 'section' : 'frame'
    const entityName = isSectionRoute
      ? (currentSectionName ?? 'Section')
      : (currentFrameName ?? 'Frame')
    const pageName =
      preview.pages.entries.find(p => p.uuid === pageUUID)?.name ?? 'Pages'
    const pageRoute = {
      pathname: routes.PREVIEW_PAGE_VIEW.create({
        previewID: preview.identifier,
        pageUUID,
      }),
      hash: location.hash,
    }

    return (
      <>
        <DocumentHead title={`${preview.name} - ${entityName}`} />
        <Flex alignItems="center">
          <SketchHomeButton />
          <PreviewTitle>{preview.name}</PreviewTitle>
          <BreadcrumbDivider />
          <NavigationItem icon="page" label={pageName} to={pageRoute} />
          <BreadcrumbDivider />
          <NavigationItem icon={entityIcon} label={entityName} />
        </Flex>
      </>
    )
  }

  const routeLabel = isPrototypeRoute ? 'Prototypes' : 'Pages'

  return (
    <>
      <DocumentHead title={`${preview.name} - ${routeLabel}`} />
      <Flex alignItems="center">
        <SketchHomeButton />
        <PreviewTitle>{preview.name}</PreviewTitle>
        <BreadcrumbDivider />
        <ButtonUnstyled data-testid="view-selector" {...buttonProps}>
          <NavigationItem
            icon={isPrototypeRoute ? 'prototype' : 'page'}
            label={routeLabel}
          />
          <ChevronsIcon />
        </ButtonUnstyled>
        {content}
      </Flex>
    </>
  )
}
